// Admin authoring for What's-New release notes (Phase 1) — list + compose + live
// Preview + the sign-off flow, matching the mockup's "Author & sign-off" view.
// The Preview panel renders ReleaseNoteCard — the SAME component the user popup
// renders — so preview = exact popup by construction. Edits are draft-only (the
// server enforces it; the form disables itself elsewhere). Publish opens the
// step-up password prompt (StepUpFields) and is bound to the ships-with version:
// the popup fires only when the app actually RUNS that version.
//
// v2 adds three things, all of them consequences of there being NO PERMANENT DELETE:
//   • a status filter, with archived HIDDEN BY DEFAULT — archive is the only way a note
//     leaves circulation, so archived notes accumulate and must not clutter the list
//   • "Restore to draft" on an archived note (the unarchive transition), which is now the
//     whole of the reversibility story
//   • "Keep the original publication date" on publish, offered ONLY when the note already
//     has one — for a note archived in error and restored, where a March note should still
//     read as March. It does not control re-notification; the version does.

import { useCallback, useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { C, F, M } from '../ops-board/theme';
import { StepUpFields } from './StepUp';
import ReleaseNoteCard, { ROLE_LABELS } from '../content/ReleaseNoteCard';
import {
  listContent, getContent, createContent, updateContent, transitionContent, uploadContentAsset,
  type ContentItemOut, type ContentAssetOut, type ContentStep, type ContentStatus, type TransitionAction,
} from '../../lib/content';

const AUDIENCE = ['all', 'pilot', 'instructor', 'ops_controller', 'haamc', 'admin', 'student', 'client'];

const STATUS_CFG: Record<ContentStatus, { label: string; color: string; bg: string }> = {
  draft: { label: 'Draft', color: C.textDim, bg: C.pill },
  ready_for_review: { label: 'Ready for review', color: C.amber, bg: C.amberLight },
  approved: { label: 'Approved', color: C.accent, bg: C.accentLight },
  published: { label: 'Published', color: C.green, bg: C.greenLight },
  archived: { label: 'Archived', color: C.textMuted, bg: '#f1f5f9' },
};

const inp: CSSProperties = { width: '100%', border: `1px solid ${C.borderMed}`, borderRadius: 7, padding: '7px 9px', fontSize: 12, fontFamily: F, boxSizing: 'border-box' };
const lbl: CSSProperties = { display: 'block', fontSize: 11, fontWeight: 700, color: C.textDim, margin: '10px 0 3px' };
const panel: CSSProperties = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 };
const btn = (primary: boolean, disabled = false): CSSProperties => ({
  border: primary ? 'none' : `1px solid ${C.border}`, borderRadius: 8, padding: '8px 14px', fontSize: 12,
  fontWeight: 700, cursor: disabled ? 'default' : 'pointer', fontFamily: F, opacity: disabled ? 0.5 : 1,
  background: primary ? C.accent : 'none', color: primary ? '#fff' : C.textDim,
});

interface FormState {
  title: string;
  version: string;
  summary: string;
  body: string;
  section: string;
  audience_roles: string[];
  steps: ContentStep[];
  hero_asset_id: string | null;
}

const emptyForm: FormState = { title: '', version: '', summary: '', body: '', section: '', audience_roles: ['all'], steps: [], hero_asset_id: null };

const toForm = (it: ContentItemOut): FormState => ({
  title: it.title,
  version: it.version ?? '',
  summary: it.summary ?? '',
  body: it.body ?? '',
  section: it.section ?? '',
  audience_roles: it.audience_roles.length ? it.audience_roles : ['all'],
  steps: it.steps ?? [],
  hero_asset_id: it.hero_asset_id,
});

export default function WhatsNewManager({ setToast }: { setToast: (m: string | null) => void }) {
  const [items, setItems] = useState<ContentItemOut[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [status, setStatus] = useState<ContentStatus>('draft');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [assets, setAssets] = useState<ContentAssetOut[]>([]); // all assets touching the open item (preview URLs)
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [keepDate, setKeepDate] = useState(false);
  // The open item's existing publication date, if any — decides whether the "keep the
  // original publication date" option is offered at all (there is nothing to keep on a
  // first publish). Held separately from `form`, which is only the editable fields.
  const [publishedAt, setPublishedAt] = useState<string | null>(null);
  // List filter (v2 §1). Client-side: listContent() already returns every status in one
  // take:200 read, so there is no reason to add a server query param for it.
  const [filter, setFilter] = useState<ContentStatus | 'all'>('all');
  const [includeArchived, setIncludeArchived] = useState(false);

  const refresh = useCallback(() => {
    listContent().then((rows) => { setItems(rows); setListError(null); }).catch((e) => setListError(e instanceof Error ? e.message : 'Failed to load.'));
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const openNew = () => { setEditingId('new'); setStatus('draft'); setForm(emptyForm); setAssets([]); setFormError(null); setPublishOpen(false); setPassword(''); setKeepDate(false); setPublishedAt(null); };
  const openItem = async (id: string) => {
    try {
      const it = await getContent(id);
      setEditingId(it.id); setStatus(it.status); setForm(toForm(it)); setAssets(it.assets ?? []); setFormError(null); setPublishOpen(false); setPassword(''); setKeepDate(false); setPublishedAt(it.published_at);
    } catch (e) { setToast(null); setListError(e instanceof Error ? e.message : 'Failed to open.'); }
  };

  const editable = editingId === 'new' || status === 'draft';
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const payload = () => ({
    title: form.title,
    version: form.version.trim() === '' ? null : form.version.trim(),
    summary: form.summary.trim() === '' ? null : form.summary,
    body: form.body.trim() === '' ? null : form.body,
    section: form.section.trim() === '' ? null : form.section,
    audience_roles: form.audience_roles,
    steps: form.steps.length ? form.steps : null,
    hero_asset_id: form.hero_asset_id,
    asset_ids: assets.map((a) => a.id),
  });

  const save = async (): Promise<string | null> => {
    setBusy(true); setFormError(null);
    try {
      const saved = editingId === 'new' ? await createContent(payload()) : await updateContent(editingId as string, payload());
      setEditingId(saved.id); setStatus(saved.status); setAssets(saved.assets ?? assets);
      refresh();
      setToast('Saved.');
      return saved.id;
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed.');
      return null;
    } finally { setBusy(false); }
  };

  const doTransition = async (action: TransitionAction) => {
    if (!editingId || editingId === 'new') return;
    setBusy(true); setFormError(null);
    // A draft transition ships whatever is on screen: save first so Ready/preview match.
    if (editable) {
      const id = await save();
      if (!id) { setBusy(false); return; }
    }
    const r = await transitionContent(
      editingId as string, action,
      action === 'publish' ? password : undefined,
      action === 'publish' ? keepDate : false,
    );
    setBusy(false);
    if (r.error) {
      setFormError(r.error);
      return; // STEP_UP_REQUIRED/FAILED keeps the publish prompt open for another try
    }
    if (r.item) { setStatus(r.item.status); setAssets(r.item.assets ?? assets); setPublishedAt(r.item.published_at); }
    setPublishOpen(false); setPassword(''); setKeepDate(false);
    refresh();
    setToast(
      action === 'publish' ? `Published — goes live to users on v${form.version}.`
        : action === 'unarchive' ? 'Restored to draft — re-approve and re-publish to put it back in front of users.'
          : 'Updated.',
    );
  };

  const upload = async (file: File, target: 'hero' | number) => {
    setBusy(true); setFormError(null);
    try {
      const asset = await uploadContentAsset(file);
      setAssets((a) => [...a, asset]);
      if (target === 'hero') set('hero_asset_id', asset.id);
      else setForm((f) => ({ ...f, steps: f.steps.map((s, i) => (i === target ? { ...s, asset_id: asset.id } : s)) }));
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Upload failed.');
    } finally { setBusy(false); }
  };

  const fileInput = (target: 'hero' | number, label: string) => (
    <label style={{ display: 'inline-block', border: `1.5px dashed ${C.borderMed}`, borderRadius: 8, padding: '5px 10px', fontSize: 11, color: C.textMuted, cursor: editable ? 'pointer' : 'default', background: C.surfaceAlt }}>
      ⬆ {label}
      <input
        type="file" accept="image/png,image/jpeg" disabled={!editable || busy} style={{ display: 'none' }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f, target); e.target.value = ''; }}
      />
    </label>
  );

  // ── List view ────────────────────────────────────────────────────────────────
  if (!editingId) {
    // Archived is HIDDEN BY DEFAULT, behind the "include deactivated" toggle — archive is
    // the only way a note leaves circulation (there is no permanent delete), so archived
    // notes accumulate and must not clutter the working list. Selecting Archived directly
    // shows them regardless of the toggle: the filter is the more specific instruction.
    const visible = items.filter((it) => {
      if (filter !== 'all') return it.status === filter;
      return includeArchived || it.status !== 'archived';
    });
    const archivedCount = items.filter((it) => it.status === 'archived').length;
    const chip = (on: boolean): CSSProperties => ({
      fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '4px 10px', cursor: 'pointer', fontFamily: F,
      border: `1px solid ${on ? C.accentBorder : C.border}`, background: on ? C.accentLight : C.surface,
      color: on ? C.accent : C.textMuted,
    });
    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>What's-New release notes</div>
            <div style={{ fontSize: 12, color: C.textMuted }}>Author → sign off → publish. A published note pops up for matching users once the app runs its ships-with version.</div>
          </div>
          <button onClick={openNew} style={btn(true)}>+ New release note</button>
        </div>
        {listError && <div style={{ background: C.redLight, border: `1px solid ${C.red}`, color: C.red, borderRadius: 8, padding: '9px 12px', fontSize: 12, marginBottom: 10 }}>{listError}</div>}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          <button onClick={() => setFilter('all')} style={chip(filter === 'all')}>All</button>
          {(['draft', 'ready_for_review', 'approved', 'published', 'archived'] as ContentStatus[]).map((st) => (
            <button key={st} onClick={() => setFilter(st)} style={chip(filter === st)}>{STATUS_CFG[st].label}</button>
          ))}
          {filter === 'all' && archivedCount > 0 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, color: C.textMuted, cursor: 'pointer', marginLeft: 4 }}>
              <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
              Include deactivated ({archivedCount})
            </label>
          )}
        </div>
        <div style={{ ...panel, padding: 0 }}>
          {items.length === 0 && <div style={{ padding: 16, fontSize: 12, color: C.textMuted }}>Nothing authored yet.</div>}
          {items.length > 0 && visible.length === 0 && (
            <div style={{ padding: 16, fontSize: 12, color: C.textMuted }}>No release notes with that status.</div>
          )}
          {visible.map((it) => {
            const s = STATUS_CFG[it.status];
            return (
              <button key={it.id} onClick={() => void openItem(it.id)} style={{ display: 'flex', width: '100%', textAlign: 'left', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'none', border: 'none', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', fontFamily: F }}>
                <span style={{ fontFamily: M, fontSize: 11, color: C.textDim, minWidth: 52 }}>{it.version ? `v${it.version}` : '—'}</span>
                <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.title}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: s.color, background: s.bg, borderRadius: 999, padding: '2px 10px' }}>{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Compose + Preview & sign-off ─────────────────────────────────────────────
  const flowOrder: ContentStatus[] = ['draft', 'ready_for_review', 'approved', 'published'];
  const flowIdx = flowOrder.indexOf(status);
  return (
    <div>
      <button onClick={() => { setEditingId(null); refresh(); }} style={{ ...btn(false), marginBottom: 12 }}>‹ All release notes</button>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>

        <div style={panel}>
          <h3 style={{ margin: '0 0 4px', fontSize: 13, color: C.text }}>Compose release note</h3>
          {/* The way back differs by status, and naming the wrong one is worse than naming
              none: request_changes REJECTS an archived note (it accepts ready_for_review
              and approved only), so an archived note has no "Request changes" button at
              all — "Restore to draft" is its route back. */}
          {!editable && (
            <div style={{ fontSize: 11, color: C.amber, background: C.amberLight, border: `1px solid ${C.amberBorder}`, borderRadius: 7, padding: '6px 9px', margin: '6px 0' }}>
              Read-only — {STATUS_CFG[status].label.toLowerCase()}.{' '}
              {status === 'archived'
                ? 'Use "Restore to draft" to bring it back.'
                : status === 'published'
                  ? 'A published note is fixed — archive it to take it out of circulation.'
                  : 'Use "Request changes" to reopen the draft.'}
            </div>
          )}
          <label style={lbl}>Ships with version</label>
          <input style={inp} value={form.version} disabled={!editable} placeholder="e.g. 0.11.0" onChange={(e) => set('version', e.target.value)} />
          <label style={lbl}>Title</label>
          <input style={inp} value={form.title} disabled={!editable} onChange={(e) => set('title', e.target.value)} />
          <label style={lbl}>Summary</label>
          <textarea style={{ ...inp, minHeight: 52, resize: 'vertical' }} value={form.summary} disabled={!editable} onChange={(e) => set('summary', e.target.value)} />
          <label style={lbl}>Body (Markdown — headings, lists, **bold**, links)</label>
          <textarea style={{ ...inp, minHeight: 80, resize: 'vertical', fontFamily: M }} value={form.body} disabled={!editable} onChange={(e) => set('body', e.target.value)} />
          <label style={lbl}>Show to roles</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {AUDIENCE.map((r) => {
              const on = form.audience_roles.includes(r);
              return (
                <button key={r} disabled={!editable}
                  onClick={() => set('audience_roles', on ? (form.audience_roles.length > 1 ? form.audience_roles.filter((x) => x !== r) : form.audience_roles) : [...form.audience_roles.filter((x) => (r === 'all' ? false : x !== 'all')), r])}
                  style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '4px 10px', cursor: editable ? 'pointer' : 'default', fontFamily: F, border: `1px solid ${on ? C.accentBorder : C.border}`, background: on ? C.accentLight : C.surface, color: on ? C.accent : C.textMuted }}>
                  {ROLE_LABELS[r] ?? r}
                </button>
              );
            })}
          </div>
          <label style={lbl}>Hero image</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {fileInput('hero', form.hero_asset_id ? 'Replace hero image' : 'Upload hero image')}
            {form.hero_asset_id && editable && <button onClick={() => set('hero_asset_id', null)} style={btn(false)}>Remove</button>}
          </div>
          <label style={lbl}>Steps & screenshots</label>
          {form.steps.map((s, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontFamily: M, fontSize: 11, color: C.accent, fontWeight: 800 }}>{i + 1}.</span>
              <input style={{ ...inp, flex: 1 }} value={s.text} disabled={!editable} placeholder="Step text"
                onChange={(e) => setForm((f) => ({ ...f, steps: f.steps.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) }))} />
              {fileInput(i, s.asset_id ? '✓' : 'img')}
              {editable && <button onClick={() => setForm((f) => ({ ...f, steps: f.steps.filter((_, j) => j !== i) }))} style={{ ...btn(false), padding: '6px 9px' }}>✕</button>}
            </div>
          ))}
          {editable && form.steps.length < 10 && (
            <button onClick={() => setForm((f) => ({ ...f, steps: [...f.steps, { text: '' }] }))} style={btn(false)}>+ Add step</button>
          )}
          <label style={lbl}>Manual section</label>
          <input style={inp} value={form.section} disabled={!editable} placeholder="e.g. Bookings" onChange={(e) => set('section', e.target.value)} />
          {formError && <div style={{ marginTop: 10, background: C.redLight, border: `1px solid ${C.red}`, color: C.red, borderRadius: 8, padding: '8px 11px', fontSize: 12 }}>{formError}</div>}
          {editable && (
            <div style={{ marginTop: 12 }}>
              <button onClick={() => void save()} disabled={busy || form.title.trim() === ''} style={btn(true, busy || form.title.trim() === '')}>💾 Save draft</button>
            </div>
          )}
        </div>

        <div style={panel}>
          <h3 style={{ margin: '0 0 8px', fontSize: 13, color: C.text }}>Preview & sign-off</h3>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 12, background: C.surfaceAlt }}>
            <ReleaseNoteCard data={{ ...form, summary: form.summary || null, body: form.body || null, steps: form.steps.length ? form.steps : null }} assets={assets} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, margin: '12px 0', flexWrap: 'wrap' }}>
            {flowOrder.map((st, i) => (
              <span key={st} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                {i > 0 && <span style={{ color: C.textMuted, fontSize: 12 }}>→</span>}
                <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '4px 10px', border: `1px solid ${i === flowIdx ? C.accent : C.border}`, color: status === 'archived' ? C.textMuted : i < flowIdx ? C.green : i === flowIdx ? '#fff' : C.textMuted, background: status !== 'archived' && i === flowIdx ? C.accent : i < flowIdx ? C.greenLight : C.surface }}>
                  {STATUS_CFG[st].label}
                </span>
              </span>
            ))}
            {status === 'archived' && <span style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: '4px 10px', background: '#f1f5f9', color: C.textMuted, border: `1px solid ${C.border}` }}>Archived</span>}
          </div>
          {status === 'ready_for_review' && (
            <div style={{ padding: 10, border: `1px solid ${C.amberBorder}`, background: C.amberLight, borderRadius: 9, fontSize: 12, color: C.amber, marginBottom: 10 }}>
              ⏳ Awaiting sign-off — nothing shows to users until approved and published.
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {editingId !== 'new' && status === 'draft' && <button onClick={() => void doTransition('ready')} disabled={busy} style={btn(true, busy)}>Submit for review</button>}
            {status === 'ready_for_review' && <button onClick={() => void doTransition('approve')} disabled={busy} style={btn(true, busy)}>✓ Approve{form.version ? ` for v${form.version}` : ''}</button>}
            {(status === 'ready_for_review' || status === 'approved') && <button onClick={() => void doTransition('request-changes')} disabled={busy} style={btn(false, busy)}>Request changes</button>}
            {status === 'approved' && !publishOpen && <button onClick={() => setPublishOpen(true)} disabled={busy} style={btn(true, busy)}>🚀 Publish…</button>}
            {status !== 'archived' && editingId !== 'new' && <button onClick={() => void doTransition('archive')} disabled={busy} style={btn(false, busy)}>Archive</button>}
            {/* Reversible archive (v2 §5) — restores to DRAFT, never straight to published. */}
            {status === 'archived' && <button onClick={() => void doTransition('unarchive')} disabled={busy} style={btn(true, busy)}>↩ Restore to draft</button>}
          </div>
          {status === 'archived' && (
            <p style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>
              Restoring puts this note back in the draft state. It has to be re-approved and re-published before users see it again.
            </p>
          )}
          {publishOpen && status === 'approved' && (
            <div style={{ marginTop: 4 }}>
              <StepUpFields password={password} setPassword={setPassword} />
              {/* Offered only when there IS an original date to keep — i.e. this note has
                  been published before and was archived and restored. Default (unchecked)
                  is today's date, because re-publishing is normally a re-broadcast. */}
              {publishedAt && (
                <div style={{ marginTop: 10, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 10px', background: C.surfaceAlt }}>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 7, fontSize: 12, color: C.textDim, cursor: 'pointer' }}>
                    <input type="checkbox" checked={keepDate} onChange={(e) => setKeepDate(e.target.checked)} style={{ marginTop: 2 }} />
                    <span>
                      Keep the original publication date
                      <span style={{ color: C.textMuted }}> ({new Date(publishedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })})</span>
                    </span>
                  </label>
                  <p style={{ fontSize: 11, color: C.textMuted, margin: '6px 0 0', lineHeight: 1.45 }}>
                    The date does not control who sees this note — the version does. Users who have already
                    seen v{form.version || '(set a version)'} will not be shown it again unless you raise the version,
                    whichever date you pick.
                  </p>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={() => void doTransition('publish')} disabled={busy || password === ''} style={btn(true, busy || password === '')}>Confirm publish</button>
                <button onClick={() => { setPublishOpen(false); setPassword(''); setKeepDate(false); }} disabled={busy} style={btn(false, busy)}>Cancel</button>
              </div>
              <p style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>
                Publishing is a broadcast — it goes live to matching users as soon as the app runs v{form.version || '(set a version)'}.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
